import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Calendar, Linkedin, FileText, CheckCircle, Loader2, Download } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    inquiry_type: 'general',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [capabilityDeck, setCapabilityDeck] = useState(null);
  const [deckLoading, setDeckLoading] = useState(false);

  useEffect(() => {
    // Check if capability deck is available
    const checkDeck = async () => {
      try {
        const response = await axios.get(`${API}/capability-deck`);
        setCapabilityDeck(response.data);
      } catch {
        setCapabilityDeck(null);
      }
    };
    checkDeck();
  }, []);

  const handleDownloadDeck = () => {
    if (capabilityDeck && capabilityDeck.file_data) {
      setDeckLoading(true);
      try {
        const link = document.createElement('a');
        link.href = capabilityDeck.file_data;
        link.download = capabilityDeck.filename || 'capability-deck.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Download started!');
      } catch {
        toast.error('Failed to download');
      } finally {
        setDeckLoading(false);
      }
    } else {
      toast.info('Capability deck coming soon! Please reach out via the contact form for more information.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        message: '',
        inquiry_type: 'general',
      });
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-teal font-mono text-sm uppercase tracking-widest mb-4">
            Get in Touch
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6">
            Let's Build the Future of AI Together
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            I partner with organizations that want to move beyond AI experimentation 
            and build real AI-powered platforms, products, and operating models.
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-teal to-orange mx-auto rounded-full mt-8" />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Card className="glass-light border-0">
              <CardContent className="p-6 lg:p-8">
                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-16 h-16 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-teal" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Thank you for reaching out. I'll get back to you soon.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setIsSubmitted(false)}
                      className="border-teal/30"
                    >
                      Send Another Message
                    </Button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                          className="glass-light border-teal/20 focus:border-teal"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                          className="glass-light border-teal/20 focus:border-teal"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company">Company</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your company"
                          className="glass-light border-teal/20 focus:border-teal"
                          data-testid="contact-company-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="inquiry_type">Inquiry Type</Label>
                        <Select
                          value={formData.inquiry_type}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, inquiry_type: value }))}
                        >
                          <SelectTrigger 
                            className="glass-light border-teal/20"
                            data-testid="contact-inquiry-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General Inquiry</SelectItem>
                            <SelectItem value="consulting">Consulting Engagement</SelectItem>
                            <SelectItem value="speaking">Speaking Opportunity</SelectItem>
                            <SelectItem value="partnership">Partnership</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell me about your AI transformation goals..."
                        rows={5}
                        className="glass-light border-teal/20 focus:border-teal resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>

                    {error && (
                      <p className="text-destructive text-sm">{error}</p>
                    )}

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-teal hover:bg-teal-dark"
                      data-testid="contact-submit-button"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Right - CTA Cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Schedule Card */}
            <Card className="glass-light border-0 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-teal/10 flex items-center justify-center shrink-0">
                  <Calendar className="w-7 h-7 text-teal" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold mb-1">
                    Schedule a Conversation
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Book a time to discuss your AI transformation needs
                  </p>
                </div>
                <Button 
                  className="bg-teal hover:bg-teal-dark shrink-0"
                  data-testid="schedule-cta-button"
                  asChild
                >
                  <a href="https://calendly.com/jsethcushing/30min" target="_blank" rel="noopener noreferrer">
                    Book Now
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* LinkedIn Card */}
            <Card className="glass-light border-0 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-blue/10 flex items-center justify-center shrink-0">
                  <Linkedin className="w-7 h-7 text-blue" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold mb-1">
                    Connect on LinkedIn
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Follow for AI transformation insights and updates
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-blue/30 hover:bg-blue/10 shrink-0"
                  asChild
                  data-testid="linkedin-cta-button"
                >
                  <a href="https://www.linkedin.com/in/seth-cushing-cap-pmp-aipo-259ba1124/" target="_blank" rel="noopener noreferrer">
                    View Profile
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* Capability Deck Card */}
            <Card className="glass-light border-0 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-orange/10 flex items-center justify-center shrink-0">
                  <FileText className="w-7 h-7 text-orange" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold mb-1">
                    Download Capability Deck
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Detailed overview of services and expertise
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-orange/30 hover:bg-orange/10 shrink-0"
                  data-testid="download-cta-button"
                  onClick={handleDownloadDeck}
                  disabled={deckLoading}
                >
                  {deckLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download
                </Button>
              </CardContent>
            </Card>

            {/* Quote */}
            <div className="glass-light rounded-xl p-6 text-center">
              <p className="text-lg font-heading italic text-foreground/80 mb-4">
                "The best time to start your AI transformation was yesterday. 
                The second best time is today."
              </p>
              <p className="text-sm text-teal font-medium">— Seth Cushing</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
